import React from "react"
import _ from "lodash"
import {
    usePurchaseCourseMutation,
    useCreateCourseOrderMutation,
    useVerifyCoursePaymentMutation
} from "../Redux/actions/lecture.action"
import { openCheckout, CheckoutDismissed } from "./razorpay"

/**
 * The buy flow, shared by the catalogue, My Courses and the course page so
 * that "buy" behaves identically wherever it is triggered.
 *
 * Free courses enrol directly. Paid courses go order -> Razorpay Checkout ->
 * verify; only the server's verify step unlocks the course.
 *
 * Returns the dialog state plus a `notification` the caller renders with the
 * shared ToastMessage component.
 */
export default function usePurchaseFlow({ onPurchased } = {}) {
    const [selected, setSelected] = React.useState(null)
    const [busy, setBusy] = React.useState(false)
    const [notification, setNotification] = React.useState({
        show: false,
        type: "",
        message: ""
    })

    const [purchaseAction] = usePurchaseCourseMutation()
    const [createOrderAction] = useCreateCourseOrderMutation()
    const [verifyAction] = useVerifyCoursePaymentMutation()

    const notify = (type, message) =>
        setNotification({ show: true, type, message })

    const startPurchase = (course) => setSelected(course)
    const cancelPurchase = () => setSelected(null)

    const payOnline = async (course) => {
        const order = await createOrderAction({
            course_id: course.course_id
        }).unwrap()

        /*
         * Close our dialog first: its focus trap would otherwise stop the
         * student typing into Razorpay's card fields.
         */
        setSelected(null)

        let result
        try {
            setBusy(false)
            result = await openCheckout(order)
        } finally {
            setBusy(true)
        }

        try {
            await verifyAction({
                course_id: course.course_id,
                ...result
            }).unwrap()
        } catch (error) {
            /* The money is taken; the webhook will still unlock the course. */
            error.paymentTaken = true
            throw error
        }
    }

    const confirmPurchase = async (course) => {
        const isFree = Number(course.price) <= 0
        setBusy(true)
        try {
            if (isFree) {
                await purchaseAction({
                    course_id: course.course_id,
                    amount: 0
                }).unwrap()
            } else {
                await payOnline(course)
            }

            setSelected(null)
            notify(
                "success",
                isFree
                    ? `"${course.title}" has been added to your courses for one year.`
                    : `Payment received — "${course.title}" is yours for one year. Happy learning!`
            )
            onPurchased?.(course)
        } catch (error) {
            if (error instanceof CheckoutDismissed) {
                notify("info", "Payment cancelled. You have not been charged.")
            } else if (error?.paymentTaken) {
                notify(
                    "warning",
                    "We received your payment but couldn't confirm it yet. The course will unlock shortly — please refresh in a few minutes."
                )
            } else if (error?.message === "load") {
                notify(
                    "error",
                    "Couldn't load the payment window. Please check your connection and try again."
                )
            } else {
                notify(
                    "error",
                    _.get(error, "data.detail", null) ||
                        "We couldn't complete that purchase. Please try again."
                )
            }
        } finally {
            setBusy(false)
        }
    }

    const clearNotification = () =>
        setNotification({ show: false, type: "", message: "" })

    return {
        selectedCourse: selected,
        isPurchasing: busy,
        startPurchase,
        cancelPurchase,
        confirmPurchase,
        notification,
        clearNotification
    }
}
